import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { dkk, num } from "@/lib/api";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from "recharts";
import { Store, X, Pause, Play, Target, TrendingUp } from "lucide-react";

const FAL = "#FB923C";
const RIN = "#38BDF8";
const GREEN = "#34D399";
const RED = "#F87171";

const SLIDE_MS = 15000;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };

const MONTHS = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const fmtDay = (p) => { const a = (p || "").split("-"); return a.length === 3 ? `${parseInt(a[2], 10)}/${parseInt(a[1], 10)}` : p; };

const SLIDES = [
  { id: "today", title: "I dag" },
  { id: "products", title: "Topprodukter" },
  { id: "categories", title: "Salg pr. kategori" },
  { id: "goals", title: "Månedsmål & overskud" },
];

function KpiBig({ label, value, color }) {
  return (
    <div className="rounded-3xl bg-[#ffffff] border border-[#e2e8f0] px-8 py-7">
      <div className="text-lg xl:text-xl uppercase tracking-[0.18em] text-slate-500 mb-3">{label}</div>
      <div className="font-heading font-light tabular-nums leading-none text-5xl xl:text-7xl" style={{ color: color || "#0f172a" }}>{value}</div>
    </div>
  );
}

function TrendTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-[#f8fafc] border border-[#e2e8f0] px-3 py-2 text-base">
      <p className="text-slate-500 mb-1">{fmtDay(label)}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}</span>
          <span className="ml-auto font-medium text-slate-800">{dkk(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Kiosk() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [clock, setClock] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [data, setData] = useState({ today: null, trend: [], products: [], categories: [], goals: [], finance: null });

  const load = useCallback(async () => {
    const today = iso(new Date());
    const from30 = iso(daysAgo(29));
    const from14 = iso(daysAgo(13));
    try {
      const [s, t, p, c, g, f] = await Promise.all([
        api.get("/kpi/summary", { params: { from: today, to: today, store: "all" } }),
        api.get("/kpi/trend", { params: { from: from14, to: today, granularity: "day" } }),
        api.get("/kpi/products", { params: { from: from30, to: today, store: "all", limit: 8 } }),
        api.get("/kpi/categories", { params: { from: from30, to: today, store: "all" } }),
        api.get("/goals"),
        api.get("/finance/overview"),
      ]);
      setData({ today: s.data, trend: t.data, products: p.data, categories: c.data, goals: g.data, finance: f.data });
      setUpdatedAt(new Date());
    } catch { /* keep last good data */ }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" }));
    tick(); const t = setInterval(tick, 10000); return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setIdx((i) => (i + 1) % SLIDES.length), SLIDE_MS);
    return () => clearTimeout(t);
  }, [idx, paused]);

  const exit = useCallback(async () => {
    try { if (document.fullscreenElement) await document.exitFullscreen(); } catch { /* */ }
    navigate("/");
  }, [navigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") exit();
      else if (e.key === "ArrowRight") setIdx((i) => (i + 1) % SLIDES.length);
      else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length);
      else if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [exit]);

  const slide = SLIDES[idx];
  const today = data.today?.totals;
  const dateLabel = (() => { const d = new Date(); return `${d.getDate()}. ${MONTHS[d.getMonth()]}`; })();
  const maxProd = Math.max(...data.products.map((p) => p.revenue), 1);
  const topCats = [...data.categories].sort((a, b) => (b.falster + b.ringsted) - (a.falster + a.ringsted)).slice(0, 8);
  const maxCat = Math.max(...topCats.map((c) => c.falster + c.ringsted), 1);
  const fin = data.finance?.combined;

  return (
    <div className="h-screen w-full bg-[#f8fafc] text-slate-800 flex flex-col overflow-hidden" data-testid="kiosk-screen">
      {/* Top bar */}
      <header className="flex items-center justify-between px-8 xl:px-12 py-5 border-b border-[#e2e8f0]">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#38BDF8] flex items-center justify-center">
            <Store className="w-6 h-6 text-[#0b1120]" strokeWidth={2} />
          </div>
          <div>
            <p className="font-heading font-bold text-2xl xl:text-3xl leading-none">Customers First</p>
            <p className="text-sm uppercase tracking-[0.25em] text-slate-500 mt-1">{slide.title} · {dateLabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          {updatedAt && <span className="text-sm text-slate-500" data-testid="kiosk-updated">Opdateret {updatedAt.toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" })}</span>}
          <span className="font-heading text-3xl xl:text-4xl tabular-nums text-slate-700" data-testid="kiosk-clock">{clock}</span>
          <button onClick={() => setPaused((p) => !p)} className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-[#eef2f6]" data-testid="kiosk-pause-btn">
            {paused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
          </button>
          <button onClick={exit} className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-[#eef2f6]" data-testid="kiosk-exit-btn">
            <X className="w-7 h-7" />
          </button>
        </div>
      </header>

      {/* Progress bar */}
      <div className="h-1.5 bg-[#ffffff]">
        <div key={idx} className="h-full bg-[#38BDF8]" style={{ animation: paused ? "none" : `kioskProgress ${SLIDE_MS}ms linear forwards` }} />
      </div>

      {/* Slide dots */}
      <div className="flex items-center justify-center gap-2 pt-4">
        {SLIDES.map((s, i) => (
          <span key={s.id} className="h-2 rounded-full transition-all duration-200" style={{ width: i === idx ? 28 : 8, background: i === idx ? "#38BDF8" : "#e2e8f0" }} />
        ))}
      </div>

      {/* Slide content */}
      <main className="flex-1 px-8 xl:px-12 py-6 xl:py-8">
        {slide.id === "today" && (
          <div className="h-full flex flex-col gap-6" data-testid="kiosk-slide-today">
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-5">
              <KpiBig label="Omsætning" value={dkk(today?.revenue)} color={GREEN} />
              <KpiBig label="Antal salg" value={num(today?.orders)} />
              <KpiBig label="Profit" value={dkk(today?.profit)} />
              <KpiBig label="Gns. kurv" value={dkk(today?.avg_basket)} />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 flex-1">
              {["falster", "ringsted"].map((s) => {
                const d = data.today?.stores?.[s];
                const c = s === "falster" ? FAL : RIN;
                return (
                  <div key={s} className="rounded-3xl bg-[#ffffff] border border-[#e2e8f0] px-8 py-6 flex flex-col justify-center" data-testid={`kiosk-store-${s}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-4 h-4 rounded-full" style={{ background: c }} />
                      <span className="text-2xl xl:text-3xl font-medium capitalize">{s}</span>
                    </div>
                    <div className="font-heading font-light tabular-nums text-5xl xl:text-6xl">{dkk(d?.revenue)}</div>
                    <div className="text-xl text-slate-500 mt-2">{num(d?.orders)} salg · gns. {dkk(d?.aov)}</div>
                  </div>
                );
              })}
              <div className="rounded-3xl bg-[#ffffff] border border-[#e2e8f0] px-6 py-5 flex flex-col">
                <div className="text-base uppercase tracking-[0.18em] text-slate-500 mb-2">14 dages omsætning</div>
                <div className="flex-1 min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="kf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={FAL} stopOpacity={0.4} /><stop offset="100%" stopColor={FAL} stopOpacity={0} /></linearGradient>
                      <linearGradient id="kr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={RIN} stopOpacity={0.4} /><stop offset="100%" stopColor={RIN} stopOpacity={0} /></linearGradient>
                    </defs>
                    <XAxis dataKey="period" tickFormatter={fmtDay} tick={{ fontSize: 13, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} minTickGap={24} interval="preserveStartEnd" />
                    <Tooltip content={<TrendTip />} />
                    <Area type="monotone" dataKey="falster_revenue" name="Falster" stroke={FAL} strokeWidth={2.5} fill="url(#kf)" dot={false} />
                    <Area type="monotone" dataKey="ringsted_revenue" name="Ringsted" stroke={RIN} strokeWidth={2.5} fill="url(#kr)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}

        {slide.id === "products" && (
          <div className="rounded-3xl bg-[#ffffff] border border-[#e2e8f0] p-8 xl:p-10 h-full" data-testid="kiosk-slide-products">
            <div className="text-lg uppercase tracking-[0.18em] text-slate-500 mb-6">Topprodukter · seneste 30 dage</div>
            {data.products.length === 0 ? (
              <p className="text-2xl text-slate-500 text-center py-20">Ingen synkroniserede varedata endnu.</p>
            ) : (
              <ul className="space-y-5">
                {data.products.slice(0, 8).map((p, i) => (
                  <li key={`${p.product}-${i}`} data-testid={`kiosk-product-${i}`}>
                    <div className="flex items-center justify-between text-2xl xl:text-3xl mb-2">
                      <span className="text-slate-700 truncate pr-4"><span className="text-slate-500 tabular-nums mr-4">{i + 1}</span>{p.product}</span>
                      <span className="font-heading font-light tabular-nums whitespace-nowrap">{dkk(p.revenue)}</span>
                    </div>
                    <div className="h-2.5 rounded bg-[#e2e8f0] overflow-hidden">
                      <div className="h-full rounded" style={{ width: `${(p.revenue / maxProd) * 100}%`, background: RIN }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {slide.id === "categories" && (
          <div className="rounded-3xl bg-[#ffffff] border border-[#e2e8f0] p-8 xl:p-10 h-full" data-testid="kiosk-slide-categories">
            <div className="flex items-center gap-8 text-lg uppercase tracking-[0.18em] text-slate-500 mb-6">
              <span>Salg pr. kategori · 30 dage</span>
              <span className="flex items-center gap-2 normal-case tracking-normal text-base"><span className="w-3 h-3 rounded-full" style={{ background: FAL }} /> Falster</span>
              <span className="flex items-center gap-2 normal-case tracking-normal text-base"><span className="w-3 h-3 rounded-full" style={{ background: RIN }} /> Ringsted</span>
            </div>
            {topCats.length === 0 ? (
              <p className="text-2xl text-slate-500 text-center py-20">Ingen synkroniserede varedata endnu.</p>
            ) : (
              <ul className="space-y-5">
                {topCats.map((c) => {
                  const total = c.falster + c.ringsted;
                  return (
                    <li key={c.category}>
                      <div className="flex items-center justify-between text-2xl xl:text-3xl mb-2">
                        <span className="text-slate-700">{c.category}</span>
                        <span className="font-heading font-light tabular-nums">{dkk(total)}</span>
                      </div>
                      <div className="h-3 rounded bg-[#e2e8f0] overflow-hidden flex">
                        <div className="h-full" style={{ width: `${(c.falster / maxCat) * 100}%`, background: FAL }} />
                        <div className="h-full" style={{ width: `${(c.ringsted / maxCat) * 100}%`, background: RIN }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {slide.id === "goals" && (
          <div className="h-full grid grid-cols-1 xl:grid-cols-2 gap-6" data-testid="kiosk-slide-goals">
            <div className="rounded-3xl bg-[#ffffff] border border-[#e2e8f0] p-8 xl:p-10">
              <div className="flex items-center gap-3 text-lg uppercase tracking-[0.18em] text-slate-500 mb-8"><Target className="w-6 h-6" /> Månedens mål</div>
              <div className="space-y-10">
                {data.goals.map((g) => {
                  const pct = Math.max(0, Math.min(g.pct ?? 0, 100));
                  const reached = (g.pct ?? 0) >= 100;
                  const c = g.store === "falster" ? FAL : RIN;
                  return (
                    <div key={g.store} data-testid={`kiosk-goal-${g.store}`}>
                      <div className="flex items-baseline justify-between mb-3">
                        <span className="text-2xl xl:text-3xl font-medium">{g.name}</span>
                        <span className="text-xl text-slate-500">{dkk(g.mtd_revenue)} / {dkk(g.monthly_goal)}</span>
                      </div>
                      <div className="h-5 rounded-full bg-[#e2e8f0] overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: reached ? GREEN : c }} />
                      </div>
                      <div className="mt-2 text-xl font-medium" style={{ color: reached ? GREEN : c }}>{g.pct ?? 0}% af målet{reached ? " — nået!" : ""}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="rounded-3xl bg-[#ffffff] border border-[#e2e8f0] p-8 xl:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-3 text-lg uppercase tracking-[0.18em] text-slate-500 mb-6"><TrendingUp className="w-6 h-6" /> Månedens overskud</div>
              <div className="font-heading font-light tabular-nums text-6xl xl:text-8xl mb-2" style={{ color: (fin?.surplus ?? 0) >= 0 ? GREEN : RED }} data-testid="kiosk-surplus">{dkk(fin?.surplus)}</div>
              <div className="text-xl text-slate-500 mb-8">Fortjeneste {dkk(fin?.profit)} − faste udgifter {dkk(fin?.expenses)}</div>
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-xl text-slate-500">Dækning af faste udgifter</span>
                <span className="text-2xl font-medium" style={{ color: (fin?.coverage_pct ?? 0) >= 100 ? GREEN : "#FBBF24" }}>
                  {fin?.coverage_pct == null ? "—" : fin.coverage_pct > 100 ? ">100%" : `${fin.coverage_pct}%`}
                </span>
              </div>
              <div className="h-5 rounded-full bg-[#e2e8f0] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(fin?.coverage_pct ?? 0, 100))}%`, background: (fin?.coverage_pct ?? 0) >= 100 ? GREEN : "#FBBF24" }} />
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
